import { useEffect, useMemo, useState, useRef } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client";
import DEFAULT_OPERATIONS from "./category.gql";
import { useSort } from "../../../hooks/useSort";
import { useDispatch, useSelector } from "react-redux";
import { saveMegaMenu } from "../../../features/MegaMenu/MegaMenuSlice";
import { useMegaMenu } from "../../MegaMenu/useMegaMenu";
import {
  saveShowEnquireNow,
  saveShowEnquirePrdouctName,
} from "../../../features/HomePage/HomePageSlice";

export const useCategory = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [loadingState, setLoadingState] = useState("idle");
  const [filters, setFilters] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const { ConfigData } = useSelector((state) => state.homePageSlice);
  const [searchParams] = useSearchParams("page");
  const paths = location?.pathname.split("/").filter((path) => path !== "");
  const pageNumberParam = searchParams.get("page") || 1;

  const { fullPageLoading } = useSelector(
    (state) => state.fullPageLoadingSlice
  );

  const { MegaMenuData, megaMenuLoading } = useMegaMenu();
  const CategoryId = useMemo(
    () => findCategoryIdByName(MegaMenuData?.category, id),
    [MegaMenuData, id]
  );

  const initialFilter = useMemo(
    () => ({
      category_id: { eq: String(CategoryId) },
    }),
    [CategoryId]
  );

  const { getCategoryQuery, getFilterInputsQuery, getPageSize } =
    DEFAULT_OPERATIONS;

  const { data: pageSizeData } = useQuery(getPageSize, {
    fetchPolicy: "cache-and-network",
    context: { fetchOptions: { method: "GET" } },
  });

  const pageSize = pageSizeData?.storeConfig?.grid_per_page || 12;

  const sortProps = useSort();
  const [currentSort] = sortProps;

  const [
    runQuery,
    { called: categoryCalled, loading: categoryLoading, error, data },
  ] = useLazyQuery(getCategoryQuery, {
    fetchPolicy: "cache-and-network",
    skip: !CategoryId,
    context: { fetchOptions: { method: "GET" } },
    onCompleted: () => {
      setLoadingState("finished");
    },
  });

  const { data: introspectionData, loading: introspectionLoading } = useQuery(
    getFilterInputsQuery,
    {
      fetchPolicy: "cache-and-network",
      context: { fetchOptions: { method: "GET" } },
    }
  );

  const filterTypeMap = useMemo(() => {
    if (!introspectionData) return new Map();
    return introspectionData.__type.inputFields.reduce(
      (acc, { name, type }) => {
        acc.set(name, type.name);
        return acc;
      },
      new Map()
    );
  }, [introspectionData]);

  useEffect(() => {
    if (!filterTypeMap.size || !pageSize) {
      return;
    }
    setFilters((prevFilters) => ({
      ...prevFilters,
      category_id: { eq: String(CategoryId) },
    }));
  }, [CategoryId, filterTypeMap, pageSize]);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchInput(inputValue);
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setFilters((prevFilters) => {
        if (inputValue === "") {
          const { name, ...restFilters } = prevFilters;
          return restFilters;
        }
        if (inputValue.length >= 3) {
          return { ...prevFilters, name: { match: inputValue } };
        }
        return prevFilters;
      });
    }, 1000);
  };

  useEffect(() => {
    const isCategoryIdDifferentFromFilter =
      filters.category_id?.eq === String(CategoryId);

    if (
      filterTypeMap.size &&
      CategoryId &&
      pageNumberParam &&
      isCategoryIdDifferentFromFilter
    ) {
      runQuery({
        variables: {
          currentPage: Number(pageNumberParam),
          id: CategoryId,
          filters,
          pageSize: Number(pageSize),
          sort: { [currentSort.sortAttribute]: currentSort.sortDirection },
        },
      });
    }
  }, [
    pageNumberParam,
    filterTypeMap.size && currentSort,
    CategoryId,
    pageSize,
    filters,
  ]);

  useEffect(() => {
    if (error && !categoryLoading && !data && pageNumberParam !== 1) {
      const currentUrl = location.pathname;
      const pageNum = `?page=${1}`;
      const newURL = encodeURI(currentUrl + pageNum);
      navigate(newURL);
    }
  }, [error, categoryLoading, data, pageNumberParam]);

  useEffect(() => {
    dispatch(saveMegaMenu(true));
  }, [dispatch]);

  const parentCategoryId = useMemo(
    () => findCategoryIdByName(MegaMenuData?.category, paths[1]),
    [MegaMenuData, id]
  );

  const isValid = ConfigData?.enquire_now_category?.includes(CategoryId);

  useEffect(() => {
    if (isValid) {
      dispatch(saveShowEnquireNow(true));
    } else {
      dispatch(saveShowEnquireNow(false));
    }
  }, [isValid]);

  useEffect(() => {
    if (id) {
      dispatch(saveShowEnquirePrdouctName(id));
    }
  }, [id]);

  useEffect(() => {
    if (categoryLoading) {
      setLoadingState("loading");
    }
  }, [categoryLoading]);

  const categoryData = !categoryLoading && data ? data : null;
  const metaDescription = data?.category?.meta_description || "";
  const metaTitle = data?.category?.meta_title || "";

  const loading = useMemo(
    () => introspectionLoading || (categoryLoading && !data),
    [introspectionLoading, categoryLoading, data]
  );

  return {
    id: CategoryId,
    categoryName: id,
    error,
    loading,
    loadingState,
    categoryData,
    metaTitle,
    metaDescription,
    pageNumberParam,
    sortProps,
    pageSize,
    setFilters,
    filters,
    initialFilter,
    fullPageLoading,
    MegaMenuData,
    megaMenuLoading,
    searchInput,
    setSearchInput,
    handleInputChange,
  };
};

const findCategoryIdByName = (category, categoryName) => {
  if (!category) return null;
  if (category.url_key === categoryName) return category.id;
  if (Array.isArray(category.children)) {
    for (const child of category.children) {
      const childResult = findCategoryIdByName(child, categoryName);
      if (childResult) return childResult;
    }
  }
  return null;
};
